import { all, call, put, takeEvery } from "redux-saga/effects"
import { withCallback } from "redux-saga-callback"

import apiClient from "@api/client"
import { Routes } from "@basics/routes"
import { INewUserPasswordAction, ISendEmailToUsersAction, UserManagementActionTypes } from "@redux/actions/userManagement"
import { UNKNOWN_REQUEST_ERROR } from "@redux/common/constants"
import {
  newSingleEntityUsecaseRequestRunningAction,
  newSingleEntityUsecaseRequestSuccessAction,
} from "@redux/common/entityRequest/actions"
import { EntityType, ScopeTypes } from "@redux/common/reduxTypes"
import { showErrorsInTestEnvironment } from "@redux/common/sagaErrorHelpers"
import { usecaseRequestRunningAction, usecaseRequestSuccessAction } from "@redux/common/scopedRequest/actions"
import { SubmissionError } from "@services/submissionError"
import { BASE_URL } from "config"

export function* usersWatcherSaga(): any {
  yield all([
    takeEvery(UserManagementActionTypes.NewUserPassword, withCallback(newUserPasswordSaga)),
    takeEvery(UserManagementActionTypes.SendEmailToUsers, sendEmailToUsersSaga)
  ])
}


/**
 * saga to set a new password by a user
 */
function* newUserPasswordSaga(action: INewUserPasswordAction) {
  try {
    // the API uses {{param}} as placeholder while Next uses [param]:
    const route = Routes.ConfirmPasswordReset.replace(/\[/g, "{{").replace(/\]/g, "}}")
    const verificationUrl = BASE_URL + route

    yield put(usecaseRequestRunningAction(ScopeTypes.UserOperation))
    // verification-URL is given to the API to be included into the mail
    const res: boolean = yield call(apiClient.newPassword, action.user, { verificationUrl })
    yield put(usecaseRequestSuccessAction(ScopeTypes.UserOperation, action.user))
    yield call(action.actions.onSuccess, res)

    return res
  } catch (err) {
    if (err instanceof SubmissionError) {
      yield call(action.actions.setErrors, err.errors)
    } else if (err instanceof Error) {
      yield call(action.actions.setErrors, { error: err.message })
    }

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR
    showErrorsInTestEnvironment("newUserPasswordSaga", errorMessage, action, err)
    yield put(usecaseRequestRunningAction(ScopeTypes.UserOperation, errorMessage))
    yield call(action.actions.setSubmitting, false)

    return null
  }
}
/**
 * saga to send an email to all or a selection of users by a community manager, who will get a confirmation email
 */
function* sendEmailToUsersSaga(action: ISendEmailToUsersAction) {
  const { onSuccess, setErrors, setSubmitting } = action.actions || {}
  const usecaseKey = action.type // @TODO fixme: usecaseKey may be refactored into action, see loadModelAction etc
  try {

    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.User, usecaseKey))
    yield call(apiClient.sendEmailToUsers, action.currentProgram.id, action.userEmail)
    yield put(newSingleEntityUsecaseRequestSuccessAction(EntityType.User, usecaseKey, null))
    // tell formik, that message was submitted
    if (setSubmitting) {
      yield call(setSubmitting, false)
    }
    // call onSuccess, if defined
    if (onSuccess) {
      yield call(action.actions.onSuccess)
    }
    return true
  } catch (err) {
    if (err instanceof Error) {
      if (err instanceof SubmissionError) {
        yield call(setErrors, err.errors)
      } else {
        yield call(setErrors, { error: err.message })
      }
    }

    const errorMessage = err instanceof Error ? err.message : UNKNOWN_REQUEST_ERROR
    showErrorsInTestEnvironment("sendEmailToUsersSaga", errorMessage, action, err)
    yield put(newSingleEntityUsecaseRequestRunningAction(EntityType.User, usecaseKey, errorMessage))
    if (setSubmitting) {
      yield call(setSubmitting, false)
    }

    return false
  }
}
